import {DatePipe, DecimalPipe} from '@angular/common';
import {HttpErrorResponse} from '@angular/common/http';
import {Component, inject, input, OnInit} from '@angular/core';
import {MatIcon} from '@angular/material/icon';
import {MatTooltip} from '@angular/material/tooltip';
import { AuthenticationService } from '@core/auth/authentication.service';
import { HasPermissionDirective } from '@shared/directives/has-permission.directive';
import {PageHeaderComponent} from '@shared/components/common/page-header/page-header.component';
import {DeleteTrigger} from '@shared/components/ui/delete-trigger/delete-trigger';
import {
  DataTable,
  FilterDate,
  FiltersContainer,
  Pagination,
  SearchInput,
  TableCellDirective,
  TableColumn,
} from '@shared/components/ui/list-components';
import {ErrorService} from '@shared/services/error.service';
import {SnackbarService} from '@shared/services/snackbar.service';
import {CrearPresupuesto} from './crear-presupuesto/crear-presupuesto';
import {PresupuestosClienteService} from './presupuestos-cliente.service';
import {PresupuestoCliente, PresupuestoClienteFiltros} from './presupuestos.interface';
import {DialogService} from '@shared/services/dialog.service';

@Component({
  selector: 'app-presupuestos',
  imports: [
    HasPermissionDirective,
    DatePipe,
    DecimalPipe,
    MatIcon,
    MatTooltip,
    PageHeaderComponent,
    DeleteTrigger,
    Pagination,
    SearchInput,
    FilterDate,
    DataTable,
    TableCellDirective,
    FiltersContainer,
  ],
  templateUrl: './presupuestos.html',
})
export class Presupuestos implements OnInit {
  readonly permissionAuth = inject(AuthenticationService);

  clienteId = input.required<number>();

  loading = false;
  deleting = false;
  deletingIndex: number | null = null;
  downloadingId: number | null = null;
  presupuestos: PresupuestoCliente[] = [];

  // Paginación
  currentPage = 1;
  lastPage = 1;
  perPage = 15;
  total = 0;

  filtros: PresupuestoClienteFiltros = {
    busqueda: '',
    fecha_desde: '',
    fecha_hasta: ''
  };

  columns: TableColumn[] = [
    { key: 'presupuesto_nro', header: 'Número' },
    { key: 'fecha', header: 'Fecha' },
    { key: 'monto', header: 'Monto' },
    { key: 'seguimiento', header: 'Seguimiento' },
    { key: 'archivo', header: 'Archivo' },
    { key: 'descripcion', header: 'Descripción' },
    { key: 'acciones', header: 'Acciones', width: '1%' },
  ];

  private presupuestosService = inject(PresupuestosClienteService);
  private errorService = inject(ErrorService);
  private snackbarService = inject(SnackbarService);
  private dialogService = inject(DialogService);

  ngOnInit(): void {
    this.getPresupuestos();
  }

  getPresupuestos(page: number = 1): void {
    this.loading = true;

    this.presupuestosService.index(this.clienteId(), page, this.perPage, this.filtros).subscribe({
      next: (response) => {
        if (response.status && response.data) {
          this.presupuestos = response.data.data;
          this.currentPage = response.data.pagination.current_page;
          this.lastPage = response.data.pagination.last_page;
          this.total = response.data.pagination.total;
        }
      },
      error: (error: HttpErrorResponse) => {
        this.errorService.handleError(error);
      }
    }).add(() => {
      this.loading = false;
    });
  }

  // Filtros
  onSearchChange(value: string): void {
    this.filtros.busqueda = value;
    this.getPresupuestos();
  }

  onFechaDesdeChange(value: string): void {
    this.filtros.fecha_desde = value;
    this.getPresupuestos();
  }

  onFechaHastaChange(value: string): void {
    this.filtros.fecha_hasta = value;
    this.getPresupuestos();
  }

  get hasActiveFilters(): boolean {
    return Object.values(this.filtros).some(valor => valor !== null && valor !== undefined && valor !== '');
  }

  clearFilters(): void {
    this.filtros = {busqueda: '', fecha_desde: '', fecha_hasta: ''};
    this.getPresupuestos();
  }

  // Paginación
  onPageChange(page: number): void {
    this.getPresupuestos(page);
  }

  onPerPageChange(perPage: number): void {
    this.perPage = perPage;
    this.getPresupuestos(1);
  }

  onRowClick(event: { item: PresupuestoCliente; index: number }): void {
    this.openEditModal(event.item);
  }

  openCreateModal(): void {
    this.openModal(null);
  }

  openEditModal(presupuesto: PresupuestoCliente): void {
    this.openModal(presupuesto);
  }

  private openModal(presupuesto: PresupuestoCliente | null): void {
    this.dialogService.openForm(CrearPresupuesto, {
      size: 'md',
      data: {
        clienteId: this.clienteId(),
        presupuesto
      }
    }).afterClosed().subscribe(result => {
      if (result) {
        this.getPresupuestos(this.currentPage);
      }
    });
  }

  descargar(presupuesto: PresupuestoCliente, event: Event): void {
    event.stopPropagation();

    if (!presupuesto.tiene_archivo) {
      return;
    }

    this.downloadingId = presupuesto.id;

    this.presupuestosService.descargar(
      presupuesto.id,
      presupuesto.archivo_nombre || `presupuesto-${presupuesto.id}`
    ).subscribe({
      next: () => this.snackbarService.showSnackBar('Presupuesto descargado correctamente'),
      error: (error: HttpErrorResponse) => this.errorService.handleError(error),
    }).add(() => {
      this.downloadingId = null;
    });
  }

  delete(id: number, index: number): void {
    this.deleting = true;
    this.deletingIndex = index;

    this.presupuestosService.destroy(id).subscribe({
      next: (response) => {
        if (response.status) {
          this.snackbarService.showSnackBar('Presupuesto eliminado correctamente');
          this.getPresupuestos(this.currentPage);
        }
      },
      error: (error: HttpErrorResponse) => {
        this.errorService.handleError(error);
      }
    }).add(() => {
      this.deleting = false;
      this.deletingIndex = null;
    });
  }
}
